import CommonJs from '@/utils/commonJs';
import type { EditCongViecsRequest } from '@/types/action-plan/ActionPlan/EditCongViecsRequest';

const SERVER_TZ = 'Asia/Bangkok';
const AP_TASK_DATE_KEYS = new Set(['ngaybatdau', 'ngayketthuc']);
const AP_API_DATE_SUFFIX = 'T17:00:00.000Z';

function parsePlainOrDmyYmd(trimmed: string): string | null {
  const plain = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (plain) return `${plain[1]}-${plain[2]}-${plain[3]}`;
  const dmy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!dmy) return null;
  const d = Number(dmy[1]);
  const mo = Number(dmy[2]);
  const y = Number(dmy[3]);
  if (d < 1 || d > 31 || mo < 1 || mo > 12 || y < 1000) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function localYmdFromDate(dt: Date): string | null {
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Calendar date in local timezone as YYYY-MM-DD.
 * Plain YYYY-MM-DD strings are unchanged; ISO with time uses local Y/M/D after Date parse.
 */
export function formatDateOnlyLocalYMD(input: Date | string | null | undefined): string | null {
  if (input == null || input === '') return null;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    const parsed = parsePlainOrDmyYmd(trimmed);
    if (parsed) return parsed;
    return localYmdFromDate(new Date(trimmed));
  }
  const dt = input instanceof Date ? input : new Date(input as string);
  return localYmdFromDate(dt);
}

/**
 * Ngày lịch Action Plan từ API / record.
 * ISO có Z: lấy YYYY-MM-DD trước T (khớp LinkedDatePicker — tránh +1 ngày ở VN).
 * Date: lấy ngày lịch local (sau normalize qua toActionPlanCalendarDate).
 */
export function actionPlanCalendarYMD(input: Date | string | null | undefined): string | null {
  if (input == null || input === '') return null;

  if (typeof input === 'string') {
    const trimmed = input.trim();
    const parsed = parsePlainOrDmyYmd(trimmed);
    if (parsed) return parsed;
    if (trimmed.includes('T') && trimmed.length >= 10) {
      if (trimmed.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(trimmed)) {
        return trimmed.slice(0, 10);
      }
      const datePart = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (datePart) return `${datePart[1]}-${datePart[2]}-${datePart[3]}`;
    }
    const dt = new Date(trimmed);
    if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
    return null;
  }

  const dt = input instanceof Date ? input : new Date(input as string);
  return localYmdFromDate(dt);
}

/** Parse chuỗi hiển thị "DD/MM/YYYY - DD/MM/YYYY" → Date local midnight. */
export function actionPlanDatesFromDisplayRange(dateTime: string | undefined): {
  ngayBatDau: Date | null;
  ngayKetThuc: Date | null;
} {
  if (!dateTime?.includes(' - ')) return { ngayBatDau: null, ngayKetThuc: null };
  const [startStr, endStr] = dateTime.split(' - ');
  const toCal = (part: string) => {
    const ymd = actionPlanCalendarYMD(part.trim());
    return ymd ? toActionPlanCalendarDate(ymd) : null;
  };
  return { ngayBatDau: toCal(startStr), ngayKetThuc: toCal(endStr) };
}

/** Local midnight đúng ngày lịch Action Plan (đọc/ghi state FE). */
export function toActionPlanCalendarDate(input: Date | string | null | undefined): Date | null {
  const ymd = actionPlanCalendarYMD(input);
  if (!ymd) return null;
  const [y, mo, d] = ymd.split('-').map(Number);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d, 0, 0, 0, 0);
}

/** ISO string gửi API — không dùng Date object để tránh JSON.stringify lệch timezone. */
export function toActionPlanApiDateIso(input: Date | string | null | undefined): string | null {
  const ymd = actionPlanCalendarYMD(input);
  return ymd ? `${ymd}${AP_API_DATE_SUFFIX}` : null;
}

/** Date từ quy ước API (dùng trước normalize hoặc khi code cần Date). */
export function toActionPlanApiDate(input: Date | string | null | undefined): Date | null {
  const iso = toActionPlanApiDateIso(input);
  if (!iso) return null;
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Hiển thị DD/MM/YYYY — cùng quy ước actionPlanCalendarYMD. */
export function formatActionPlanDateDisplay(input: Date | string | null | undefined): string {
  const ymd = actionPlanCalendarYMD(input);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

/** Duyệt sâu payload — mọi ngayBatDau/ngayKetThuc → ISO string trước khi POST. */
export function normalizeActionPlanDatesForApi(value: unknown): unknown {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map((item) => normalizeActionPlanDatesForApi(item));
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(obj)) {
      if (AP_TASK_DATE_KEYS.has(key.toLowerCase()) && val != null && val !== '') {
        out[key] = toActionPlanApiDateIso(val as Date | string) ?? val;
      } else {
        out[key] = normalizeActionPlanDatesForApi(val);
      }
    }
    return out;
  }
  return value;
}

/** Chuẩn hóa ngày trên payload editcongviecs trước khi serialize JSON. */
export function normalizeEditCongViecsRequestForApi(request: EditCongViecsRequest): EditCongViecsRequest {
  return normalizeActionPlanDatesForApi(request) as EditCongViecsRequest;
}

const ymdFromInstantInServerTz = (dt: Date): string | null => {
  if (Number.isNaN(dt.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: SERVER_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(dt);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;
  if (!y || !m || !d) return null;
  return `${y}-${m}-${d}`;
};

/**
 * Calendar date in server timezone (Asia/Bangkok) as YYYY-MM-DD.
 * ISO without offset uses the date segment as-is (matches .NET DateTime JSON).
 */
export function formatDateOnlyServerYMD(input: Date | string | null | undefined): string | null {
  if (input == null || input === '') return null;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    const parsed = parsePlainOrDmyYmd(trimmed);
    if (parsed) return parsed;
    const hasTZ = /[Zz]|[+-]\d{2}:?\d{2}$/.test(trimmed);
    if (!hasTZ) {
      const datePart = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (datePart) return `${datePart[1]}-${datePart[2]}-${datePart[3]}`;
    }
    return ymdFromInstantInServerTz(new Date(trimmed));
  }
  const dt = input instanceof Date ? input : new Date(input as string);
  return ymdFromInstantInServerTz(dt);
}

/** Local midnight for the calendar day from formatDateOnlyLocalYMD. */
export function toLocalCalendarMidnight(input: Date | string | null | undefined): Date | null {
  const ymd = formatDateOnlyLocalYMD(input);
  if (!ymd) return null;
  const [y, mo, d] = ymd.split('-').map(Number);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d, 0, 0, 0, 0);
}

export function normalizeListChiTieuNgayForApi<T extends { ngay?: Date | string | null; chiTieu?: number | null }>(
  list: T[] | null | undefined
): Array<Omit<T, 'ngay'> & { ngay: string | null; id: string; ID: string }> {
  if (!list || !Array.isArray(list)) return [];
  return list.map((item) => {
    const row = item as T & { id?: string; ID?: string };
    const rawId = row.ID ?? row.id;
    const idStr =
      rawId != null && String(rawId).trim() !== '' ? String(rawId) : CommonJs.createTempID();
    return {
      ...item,
      ngay: item.ngay == null ? null : formatDateOnlyLocalYMD(item.ngay as Date | string),
      id: idStr,
      ID: idStr,
    };
  });
}
